import React, { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import lottie from 'lottie-web/build/player/lottie_light';
import { useTranslation } from 'react-i18next';
import { createGTMTracking, getGTMAttributes } from '../../utils/gtm/gtm-tracking';
import { LandingHeader } from '../layout/LandingHeader';
import { FreeTrialButton } from './FreeTrialButton';
import { Button } from '../ui/atoms/Button';

export const HeroSectionV2: React.FC = () => {
  const { t } = useTranslation('marketing');
  const animationContainer = useRef<HTMLDivElement>(null);
  const animationInstance = useRef<any>(null);

  useEffect(() => {
    if (animationContainer.current && !animationInstance.current) {
      animationInstance.current = lottie.loadAnimation({
        container: animationContainer.current,
        renderer: 'svg',
        loop: true,
        autoplay: true,
        path: '/landing-img/animation-desktop.json',
        rendererSettings: {
          preserveAspectRatio: 'xMidYMid meet',
          progressiveLoad: true,
          hideOnTransparent: true,
          className: 'lottie-animation'
        }
      });
    }

    return () => {
      if (animationInstance.current) {
        animationInstance.current.destroy();
        animationInstance.current = null;
      }
    };
  }, []);

  return (
    <section
      className="flex flex-col items-start self-stretch relative w-full min-h-svh"
      {...getGTMAttributes(createGTMTracking('SECTION_VIEWED', { origin: 'welcome_page', target: 'hero' }))}
    >
      <div
        className="absolute inset-0 w-full h-full pointer-events-none"
        style={{
          background: `
              radial-gradient(circle at top calc(50% + 10px) right calc(0% - 120px), var(--color-gradient-pink) 0%, transparent 25%),
              radial-gradient(circle at bottom calc(0% - 100px) right calc(0% - 150px), var(--color-gradient-purple) 0%, transparent 25%),
              linear-gradient(105deg, var(--color-light) 0%, var(--color-light-lin) 60%, var(--color-gradient-light) 100%)
            `
        }}
      />

      <LandingHeader />

      <motion.section
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6, delay: 0.2 }}
        className="relative w-full"
        style={{ willChange: 'opacity' }}
      >
        <div
          className="absolute w-full h-full mx-4 md:mx-6 lg:mx-8 lg:mt-3 pointer-events-none"
          style={{
            background: `radial-gradient(circle at 2px 2px, rgba(23, 29, 39, 0.15) 2px, transparent 0)`,
            backgroundSize: '50px 50px'
          }}
        />
        <div className="flex flex-col lg:flex-row items-center justify-between px-4 sm:px-6 lg:pl-24 sm:mb-12 lg:mb-10 mt-10 lg:mt-0 gap-0 sm:gap-8">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.3, ease: "easeOut" }}
            className="flex flex-col items-center lg:items-start text-center lg:text-left gap-6 sm:gap-8 w-full lg:max-w-none relative z-10"
            style={{ willChange: 'transform, opacity' }}
          >
            <div className="flex flex-col items-start lg:items-start md:items-center gap-6 sm:gap-8 self-stretch">
              <h1 className="self-stretch text-dark-invirtus text-3xl sm:text-4xl md:text-5xl lg:text-[40px] xl:text-[48px] 2xl:text-[58px] font-bold leading-[1.05] tracking-tight text-balance">
                {t('hero.headline')}
              </h1>
              <motion.div
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.5, ease: "easeOut" }}
                className="flex flex-col items-start lg:items-start md:items-center gap-3 sm:gap-4 self-stretch"
                style={{ willChange: 'transform, opacity' }}
              >
                <div className="self-stretch text-dark-invirtus/70 text-base sm:text-lg font-medium">
                  <span className="font-bold">{t('hero.tagline_bold')}</span>
                  <br />
                  <span className="font-normal">{t('hero.tagline')}</span>
                </div>
              </motion.div>
            </div>

            {/* V2: Free Trial as primary, Book a Demo as secondary */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.4, delay: 0.7, ease: "easeOut" }}
              className="w-full sm:w-auto flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center lg:justify-start items-center relative z-20"
              style={{ pointerEvents: 'auto', willChange: 'transform, opacity' }}
            >
              <FreeTrialButton origin="hero" />
              <Button
                label={t('nav.book_demo')}
                type="outlined"
                intent="secondary"
                size="md"
                onClick={() => window.open('https://calendly.com/aida-invirtus/30min', '_blank')}
                className="whitespace-nowrap !h-11 sm:!h-14 px-6 sm:px-8 !text-sm sm:!text-base"
                {...getGTMAttributes(createGTMTracking('REQUEST_DEMO', { origin: 'hero' }))}
              />
            </motion.div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.4, ease: "easeOut" }}
            className="relative w-full lg:w-auto lg:shrink-0"
            style={{ willChange: 'transform, opacity' }}
          >
            <div
              ref={animationContainer}
              className="relative w-full lg:min-w-3xl h-auto lg:left-20 lg:top-[-40px] object-contain max-w-full pointer-events-none"
              style={{
                willChange: 'transform',
                backfaceVisibility: 'hidden',
                transform: 'translateZ(0)',
                contain: 'layout style paint',
                isolation: 'isolate'
              }}
            />
          </motion.div>
        </div>
      </motion.section>
    </section>
  );
};
